import { useEffect, useState } from 'react';
import { useDataApi } from '@adsk/alloy-helpers';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import { useLocale } from '@adsk/alloy-react-helpers';
import { useFormikContext } from 'formik';
import { PERMISSION_TYPES } from '../constants';
import {
  buildGetSheetsPermissionsRequest,
  getSheetsPermissionsInfo,
} from './utils';

const useSheetsPermissions = (hasAccessToBuild) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const [permissionsInfo, setPermissionsInfo] = useState({ loading: true });
  const { values } = useFormikContext();
  const {
    member: {
      id: memberId,
      selectedAccessLevel,
      selectedCompany,
      selectedRoles,
    },
  } = values;
  const projectId = useCurrentProjectId();
  const {
    data: projectSheetPermissionsData,
    doFetch: doFetchProjectSheetPermissions,
    error: sheetsPermissionsError,
  } = useDataApi();

  // Fetch project sheet permissions when projectId changes
  useEffect(() => {
    if (!projectId || !hasAccessToBuild) return;

    doFetchProjectSheetPermissions(buildGetSheetsPermissionsRequest(projectId));
  }, [projectId, doFetchProjectSheetPermissions, hasAccessToBuild]);

  useEffect(() => {
    if (!projectSheetPermissionsData) return;

    const { results } = projectSheetPermissionsData;

    const everyonePermissions = results.find(
      (permission) =>
        permission.subjectType === PERMISSION_TYPES.GROUP.toUpperCase()
    );
    let memberPermissions;
    if (memberId) {
      memberPermissions = results.find(
        (permission) =>
          permission.subjectId === memberId &&
          permission.subjectType === PERMISSION_TYPES.USER.toUpperCase()
      );
      if (!memberPermissions) memberPermissions = everyonePermissions;
    }
    const companyPermissions = selectedCompany
      ? results.find(
          (permission) =>
            permission.subjectId === selectedCompany.id &&
            permission.subjectType === PERMISSION_TYPES.COMPANY.toUpperCase()
        )
      : null;
    const rolesPermissions =
      selectedRoles.length > 0
        ? results.filter(
            (permission) =>
              selectedRoles.some((r) => r.id === permission.subjectId) &&
              permission.subjectType === PERMISSION_TYPES.ROLE.toUpperCase()
          )
        : [];

    const info = getSheetsPermissionsInfo(
      memberPermissions,
      companyPermissions,
      rolesPermissions,
      everyonePermissions,
      selectedAccessLevel?.value,
      selectedCompany,
      selectedRoles,
      T
    );

    setPermissionsInfo(info);
  }, [
    memberId,
    projectSheetPermissionsData,
    selectedAccessLevel,
    selectedCompany,
    selectedRoles,
    T,
  ]);

  useEffect(() => {
    if (!sheetsPermissionsError) return;

    setPermissionsInfo({});
  }, [sheetsPermissionsError]);

  return permissionsInfo;
};

export default useSheetsPermissions;
